import AirDatepicker from "air-datepicker";
import { createPopper } from "@popperjs/core";
import localeEn from "air-datepicker/locale/en";
import localeNl from "air-datepicker/locale/nl";
import localeAr from "air-datepicker/locale/ar";
import localeEs from "air-datepicker/locale/es";
import localeFr from "air-datepicker/locale/fr";
import localeTr from "air-datepicker/locale/tr";
import moment from "moment";
import "moment/locale/nl";
import "moment/locale/ar";
import "moment/locale/es";
import "moment/locale/fr";
import "moment/locale/tr";

export default class DatePicker {
  constructor(node) {
    this.element = node;
    this.datepickerInput = this.element.querySelector(".js-datepicker");
    this.datepickerActualInput = this.element.querySelector(".js-datepicker-actual-input");
    this.datepickerAltInput = this.element.querySelector(".js-datepicker-alt-input");

    this.defaultView = this.element.dataset.datepickerView ? this.element.dataset.datepickerView : "days";
    this.hasRangePicker = this.element.dataset.datepickerRange;
    this.isMobile = window.matchMedia("(max-width: 767.98px)").matches;
    this.initialized = false;

    this.minimumDate = this.element.dataset.datepickerMinDate;
    this.maximumDate = this.element.dataset.datepickerMaxDate;

    this.initialize();
  }

  getLocale() {
    this.websiteLanguage = document.documentElement.lang.toLowerCase();

    // TODO: might have to add more language support
    switch (this.websiteLanguage) {
      case "en":
        this.locale = localeEn;
        break;
      case "nl":
        this.locale = localeNl;
        break;
      case "ar":
        this.locale = localeAr;
        break;
      case "es":
        this.locale = localeEs;
        break;
      case "fr":
        this.locale = localeFr;
        break;
      case "tr":
        this.locale = localeTr;
        break;
      default:
        this.locale = localeEn;
        break;
    }
  }

  getDefaultView() {
    switch (this.defaultView) {
      case "months":
        return {
          minView: "months",
          view: "months",
          dateFormat: "yyyy-MM-dd",
          altField: this.datepickerAltInput,
          altFieldDateFormat: "MMMM yyyy",
        };
      case "years":
        return {
          minView: "years",
          view: "years",
          dateFormat: "yyyy-mm-dd",
          altField: this.datepickerAltInput,
          altFieldDateFormat: "yyyy",
        };
      default:
        break;
    }
  }

  initialize() {
    this.getLocale();
    this.createErrorElement();

    this.datepicker = new AirDatepicker(this.datepickerInput, {
      position({ $datepicker, $target, $pointer, done }) {
        let popper = createPopper($target, $datepicker, {
          placement: "bottom",
          modifiers: [
            {
              name: "flip",
              options: {
                padding: {
                  top: 64,
                },
              },
            },
            {
              name: "offset",
              options: {
                offset: [0, 20],
              },
            },
            {
              name: "arrow",
              options: {
                element: $pointer,
              },
            },
          ],
        });
        return function completeHide() {
          popper.destroy();
          done();
        };
      },
      isMobile: this.isMobile,
      navTitles: {
        days: "<strong>MMMM yyyy</strong><i class='icon icon-chevron-down-bold'>",
        months: "<strong>MMMM yyyy</strong><i class='icon icon-chevron-down-bold'></i>",
        years: "<strong>yyyy1 - yyyy2</strong>",
      },
      prevHtml: "<i class='icon icon-chevron-left-bold'></i>",
      nextHtml: "<i class='icon icon-chevron-right-bold'></i>",
      locale: this.locale,
      range: this.hasRangePicker,
      maxDate: this.maximumDate,
      minDate: this.minimumDate,
      toggleSelected: false,
      firstDay: 1,
      onHide: () => {
        if (this.datepickerAltInput) {
          this.datepickerAltInput.classList.remove("border-primary");
        }
      },
      onSelect: ({ date }) => {
        if (date === undefined) {
          this.datepickerActualInput.value = "";
          return;
        }

        if (this.hasRangePicker) {
          // The date will be an array of one to two dates if range picker is enabled.
          this.datepickerActualInput.value = date.map((d) => moment(d, this.datepicker.locale.dateFormat.toUpperCase()).format("YYYY-MM-DD"));
        } else {
          this.datepickerActualInput.value = moment(date, this.datepicker.locale.dateFormat.toUpperCase()).format("YYYY-MM-DD");
        }

        this.errorElement.classList.add("d-none");

        if (!this.initialized) {
          this.initialized = true;
          return;
        }

        this.datepickerActualInput.dispatchEvent(new Event("datepicker-change"));

        if (this.isMobile) {
          this.datepicker.hide();
        }
      },
      ...this.getDefaultView(),
    });

    this.datepickerInput.placeholder = this.datepicker.locale.dateFormat;

    if (this.datepickerInput.value) {
      this.updateDatePickerValue(this.datepickerInput.value); // Set initial date via datepicker.
    }

    if (this.datepickerAltInput) {
      this.datepickerAltInput.addEventListener("click", () => {
        this.datepickerAltInput.classList.add("border-primary");
        this.datepickerInput.focus();
      });
    }

    if (this.isMobile) {
      window.addEventListener("scroll", () => {
        requestAnimationFrame(() => {
          // hide datepicker on mobile when scrolling
          if (this.datepicker.visible) {
            this.datepicker.hide();
          }
        });
      });
    }
    this.setupMoment();
  }

  setupMoment() {
    // NOTE: incase there will be more languages added this must be updated.
    moment.locale(this.websiteLanguage);

    this.datepickerInput.addEventListener("change", (e) => {
      const dateValue = e.target.value;

      this.updateDatePickerValue(dateValue);
    });
  }

  createErrorElement() {
    this.errorElement = document.createElement("div");
    this.errorElement.className = "mt-2 input-notice input-error d-none";
    this.errorElement.innerText = this.element.dataset.errorMessage;
    this.element.appendChild(this.errorElement);
  }

  updateDatePickerValue(dateValue) {
    this.errorElement.classList.add("d-none");

    const formatDateValue = moment(dateValue, this.datepicker.locale.dateFormat.toUpperCase());

    if (formatDateValue.isValid()) {
      const date = formatDateValue.format();

      this.datepickerActualInput.value = date;
      this.datepicker.selectDate(date);
      this.datepicker.setFocusDate(date);
      this.datepicker.setViewDate(date);
    } else {
      this.datepicker.clear();

      this.errorElement.classList.remove("d-none");
      this.datepickerActualInput.value = "";
    }
  }
}
